// react
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import type { PortfolioAnalysisOccupancyOutput } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';
import {
  useGetOccupancyOccupancyAnalysisTypeRecalcAccountIdMeterIdGetQuery,
  useSetManualOccupancyForMeterManualOccupancyAnalysisTypeAccountIdMeterIdPutMutation,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';

// shared components
// shared-components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import InvertedSlider from '@/components/ui/slider/InvertedSlider';

// components
import type { AnalysisType } from '@/features/energyxplorer/types';
import { showSnackbar } from '@/redux/snackbarSlice';
import { formatHour } from '@/utils/formatters';

// relative
import {
  toCalculatedMeterOccupancyFormValues,
  toEditMeterOccupancyFormValues,
  toPortfolioAnalysisOccupancy,
} from './editMeterOccupancyForm.mappers';
import {
  createEditMeterOccupancyFormSchema,
  DAYS_OF_WEEK,
} from './editMeterOccupancyForm.schema';
import type { DayKey } from './editMeterOccupancyForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

const GRANULARITY = 4;

interface EditMeterOccupancyFormProps {
  accountId: number;
  meterId: string;
  analysisType: AnalysisType;
  onSuccess?: () => void;
  onCancel?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export default function EditMeterOccupancyForm(
  props: EditMeterOccupancyFormProps,
) {
  const { accountId, meterId, analysisType } = props;

  const { data: occupancyData, isLoading } =
    useGetOccupancyOccupancyAnalysisTypeRecalcAccountIdMeterIdGetQuery({
      recalcAccountId: accountId,
      meterId,
      analysisType,
    });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Form must mount with data: react-hook-form applies late `values` only after a render.
  if (!occupancyData) return null;

  return <OccupancyFormContent {...props} occupancyData={occupancyData} />;
}

function OccupancyFormContent({
  accountId,
  meterId,
  analysisType,
  onSuccess,
  onCancel,
  onDirtyChange,
  occupancyData,
}: EditMeterOccupancyFormProps & {
  occupancyData: PortfolioAnalysisOccupancyOutput;
}) {
  const { t } = useTranslation(['forms', 'validation', 'common']);
  const dispatch = useDispatch();

  const [setOccupancy, { isLoading: isSaving }] =
    useSetManualOccupancyForMeterManualOccupancyAnalysisTypeAccountIdMeterIdPutMutation();

  const schema = createEditMeterOccupancyFormSchema(t);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toEditMeterOccupancyFormValues(occupancyData),
    values: toEditMeterOccupancyFormValues(occupancyData),
    mode: 'onTouched',
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleResetToCalculated = () => {
    if (occupancyData) {
      reset(toCalculatedMeterOccupancyFormValues(occupancyData), {
        keepDefaultValues: true,
      });
    }
  };

  const calculatedOccupancy = occupancyData?.CalculatedOccupancy;

  const calculatedOccupancyTooltip = calculatedOccupancy ? (
    <Box>
      {DAYS_OF_WEEK.map((day) => {
        const d = calculatedOccupancy[day];
        return (
          <Typography key={day} variant="caption" display="block">
            {t(`days.${day.toLowerCase()}`, {
              ns: 'common',
              defaultValue: day,
            })}
            :{' '}
            {d.occupancy
              ? `${formatHour(d.start_time ?? 8)} – ${formatHour(d.end_time ?? 16)}`
              : t('editMeterOccupancy.inactive', { ns: 'forms' })}
          </Typography>
        );
      })}
    </Box>
  ) : (
    t('editMeterOccupancy.noCalculatedOccupancy', { ns: 'forms' })
  );

  const onSubmit = handleSubmit(async (values) => {
    try {
      await setOccupancy({
        accountId,
        meterId,
        analysisType,
        occupancy: toPortfolioAnalysisOccupancy(values),
      }).unwrap();

      dispatch(
        showSnackbar({
          message: t('common.updateSuccess', { ns: 'forms' }),
          severity: 'success',
        }),
      );

      onSuccess?.();
    } catch {
      dispatch(
        showSnackbar({
          message: t('common.updateError', { ns: 'forms' }),
          severity: 'error',
        }),
      );
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <ListItemText
              primary={t('editMeterOccupancy.title', { ns: 'forms' })}
              secondary={t('editMeterOccupancy.description', { ns: 'forms' })}
            />

            <Divider sx={{ my: 2 }} />

            <Stack spacing={3}>
              {DAYS_OF_WEEK.map((day: DayKey) => (
                <Controller
                  key={day}
                  name={day}
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ minWidth: 90 }}>
                        {t(`days.${day.toLowerCase()}`, {
                          ns: 'common',
                          defaultValue: day,
                        })}
                      </Typography>

                      <Button
                        variant={field.value.active ? 'contained' : 'outlined'}
                        color={field.value.active ? 'primary' : 'inherit'}
                        size="small"
                        onClick={() =>
                          field.onChange({
                            ...field.value,
                            active: !field.value.active,
                          })
                        }
                      >
                        {field.value.active
                          ? t('editMeterOccupancy.active', { ns: 'forms' })
                          : t('editMeterOccupancy.inactive', { ns: 'forms' })}
                      </Button>

                      <Box sx={{ flex: 1 }}>
                        {field.value.active ? (
                          <InvertedSlider
                            {...({ isInverted: false } as Record<
                              string,
                              unknown
                            >)}
                            min={0}
                            max={24 * GRANULARITY}
                            step={1}
                            value={[field.value.start, field.value.end]}
                            onChange={(
                              _e: unknown,
                              newValue: number | number[],
                            ) => {
                              if (Array.isArray(newValue)) {
                                field.onChange({
                                  ...field.value,
                                  start: newValue[0],
                                  end: newValue[1],
                                });
                              }
                            }}
                            valueLabelDisplay="on"
                            valueLabelFormat={(v: number) =>
                              formatHour(v / GRANULARITY)
                            }
                            disableSwap
                          />
                        ) : (
                          <Box />
                        )}
                      </Box>
                    </Box>
                  )}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          {onCancel && (
            <CancelButton onClick={onCancel} disabled={isSaving}>
              {t('common.cancel', { ns: 'forms' })}
            </CancelButton>
          )}
          <Tooltip title={calculatedOccupancyTooltip} arrow>
            <span>
              <Button
                variant="text"
                disabled={!calculatedOccupancy || isSaving}
                onClick={handleResetToCalculated}
              >
                {t('editMeterOccupancy.resetToCalculated', { ns: 'forms' })}
              </Button>
            </span>
          </Tooltip>
          <SubmitButton
            loading={isSaving}
            disabled={!isValid || !isDirty || isSaving}
          >
            {isSaving
              ? t('editMeterOccupancy.submitting', { ns: 'forms' })
              : t('editMeterOccupancy.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
